import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReimbursementInitialState } from '../utils/Types';
import { isAPISuccess } from '../utils/Util';
import { API_STATUS, BlankPagination } from '../utils/Constant';
import {
  applyReimbursement,
  approveReimbursement,
  getApproveReimbursementList,
  getReimbursementDetails,
  getReimbursementLocationDetails,
  postReimbursementAttachments,
} from '../services';
import { getTaskList } from '../services/taskService';
import {
  getStatusDrop,
  getTasksNameDrops,
  getTargetsDropValues,
} from '../services/dropDownService';

export const initialState: ReimbursementInitialState = {
  applyReimbursementData: [],
  applyReimbursementPagination: BlankPagination,
  approveReimbursementData: [],
  approveReimbursementPagination: BlankPagination,
  reimbursementTypes: [],
  taskNameList: [],
  apiProgressStatus: '',
  statusDropOptions: [],
  approveReimbursementDetails: {},
};
export const getApplyReimbursementData = createAsyncThunk(
  'reimbursement/applyReimbursementData',
  async (data: any) => {
    const res = await getTaskList(data);
    return res?.data;
  }
);
export const getApproveReimbursementData = createAsyncThunk(
  'reimbursement/approveReimbursementData',
  async (data: any) => {
    const res = await getApproveReimbursementList(data);
    return res.data;
  }
);

export const postApplyReimbursement = createAsyncThunk(
  'reimbursement/postApplyReimbursement',
  async (data: any) => {
    const res = await applyReimbursement(data);
    return res.data;
  }
);

export const postApplyReimbursementAttachments = createAsyncThunk(
  'reimbursement/postApplyReimbursementAttachments',
  async ({ data, params }: { data: any; params: any }) => {
    const res = await postReimbursementAttachments(data, params);
    return res.data;
  }
);

export const fetchReimbursementTaskNames = createAsyncThunk(
  'reimbursement/fetchReimbursementTaskNames',
  async () => {
    const res = await getTasksNameDrops();
    return res.data;
  }
);

export const fetchReimbursementDetails = createAsyncThunk(
  'reimbursement/fetchReimbursementDetails',
  async (params: any) => {
    const res = await getReimbursementDetails(params);
    return res.data;
  }
);

export const fetchReimbursementLocationDetails = createAsyncThunk(
  'reimbursement/fetchReimbursementLocationDetails',
  async (params: any) => {
    const res = await getReimbursementLocationDetails(params);
    return res.data;
  }
);

export const approveOrRejectReimbursement = createAsyncThunk(
  'reimbursement/approveOrRejectReimbursement',
  async (data: any) => {
    const res = await approveReimbursement(data);
    return res.data;
  }
);

export const fetchReimbursementTypes = createAsyncThunk(
  'reimbursement/fetchReimbursementTypes',
  async (params: any) => {
    const res = await getTargetsDropValues(params);
    return res.data;
  }
);

export const fetchReimbursementStatusDrops = createAsyncThunk(
  'reimbursement/fetchStatusDrops',
  async (params: any) => {
    const res = await getStatusDrop(params);
    return res.data;
  }
);
const getApplyReimbursementDataFailed = (state: any) => {
  state.apiProgressStatus = API_STATUS.FAILED;
  state.applyReimbursementData = [];
  state.applyReimbursementPagination = BlankPagination;
};
const getApproveReimbursementDataFailed = (state: any) => {
  state.apiProgressStatus = API_STATUS.FAILED;
  state.approveReimbursementData = [];
  state.approveReimbursementPagination = BlankPagination;
};
export const reimbursementSlice = createSlice({
  name: 'reimbursement',
  initialState,
  reducers: {
    clearApproveReimbursementDetails: (state) => {
      state.approveReimbursementDetails = {};
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getApplyReimbursementData.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.applyReimbursementData = action.payload.result ?? [];
          state.applyReimbursementPagination =
            action.payload.pagination ?? BlankPagination;
        } else {
          getApplyReimbursementDataFailed(state);
        }
      })
      .addCase(getApplyReimbursementData.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(getApplyReimbursementData.rejected, (state) => {
        getApplyReimbursementDataFailed(state);
      })
      .addCase(getApproveReimbursementData.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.approveReimbursementData = action.payload.result ?? [];
          state.approveReimbursementPagination =
            action.payload.pagination ?? BlankPagination;
        } else {
          getApproveReimbursementDataFailed(state);
        }
      })
      .addCase(getApproveReimbursementData.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(getApproveReimbursementData.rejected, (state) => {
        getApproveReimbursementDataFailed(state);
      })
      .addCase(fetchReimbursementTypes.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.picklistId,
            label: item.picklistName,
          }));
          state.reimbursementTypes = dropdowns;
        }
      })
      .addCase(fetchReimbursementTypes.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchReimbursementTypes.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
        state.reimbursementTypes = [];
      })
      .addCase(fetchReimbursementTaskNames.fulfilled, (state, action) => {
        const taskNames = action.payload.result?.map((item: any) => {
          return {
            value: item.taskNameId,
            label: item.taskName,
          };
        });
        state.taskNameList = [...taskNames];
      })
      .addCase(fetchReimbursementStatusDrops.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.statusId,
            label: item.statusName,
          }));
          state.statusDropOptions = dropdowns;
        }
      })
      .addCase(fetchReimbursementStatusDrops.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchReimbursementStatusDrops.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
        state.statusDropOptions = [];
      })
      .addCase(fetchReimbursementDetails.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.approveReimbursementDetails = action.payload.result;
        }
      })
      .addCase(fetchReimbursementDetails.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchReimbursementDetails.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
        state.approveReimbursementDetails = {};
      });
  },
});

export const { clearApproveReimbursementDetails } = reimbursementSlice.actions;
export const reimbursementSelector = (state: {
  reimbursement: ReimbursementInitialState;
}) => state.reimbursement;

export default reimbursementSlice.reducer;
